import { testimonialMediaAdminLink, testimonialPermissionLabel } from '../../../shared/testimonial';
import { ProjectMoneyRows, ProjectMoneyStatus } from '../../components/ProjectMoneyRows';
import type { InvoiceDraftResponse } from '../../../shared/invoicing';
import { DrivePicker } from '../../components/DrivePicker';
import { Fragment, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { Link } from 'react-router-dom';
import { artifactTypeLabel } from '../../../shared/artifactTypes';
import { calendarDaysBetween, melbourneDate } from '../../../shared/dates';
import { type MoneyMode } from '../../lib/money';
import { artifactStatusLabel, artifactStatusTone, CREW_COST_ERROR_TITLE, crewFooterSub, DASHBOARD_BASE, PHASE_DOTS, pillTone, socialTone, TOOL_MAP, waitingNextStatus } from '../../lib/projectDetailShared';
import type { ResolvedProjectContact } from '../../../shared/clientContact';
import { ARTIFACT_TOOL_LABELS, PHASE_LABELS, PROJECT_PHASES, SOCIAL_STATUSES, SOCIAL_STATUS_LABELS, type Brand, type ClientContactRow, type ClientWithCounts, type CrewContactRow, type InvoiceRow, type ProjectArtifactRow, type ProjectCrewConflict, type ProjectCrewWithName, type ProjectDeliverableRow, type UnlinkedArtifactCandidate } from '../../../shared/types';
import { ContactLine, Dropdown, formatDate, formatInvoiceNumber, formatMoney, InlineDate, InlineMoney, InlineSelect, InlineText, Pill, Seg, type DropdownItem } from '../../components/primitives';
import { ArtifactTitle } from '../../components/ArtifactTitle';
import { DocumentSendsRail } from '../../components/DocumentSendsRail';
import { ReviewArtifactActions } from '../../components/ReviewArtifactActions';
import type { ProjectDetailRow, CrewBookingDraft } from './types';
import { projectContactPickerMode, ContactPicker } from './contactPicker';
import { CrewEntryRow, AddCrewBooking } from './crewBooking';

function RailGroup({
  slug,
  title,
  badge,
  status,
  children,
}: {
  slug: string;
  title: string;
  badge?: string;
  status?: React.ReactNode;
  children: React.ReactNode;
}) {
  const storageKey = `ss-os-rail-group-${slug}`;
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem(storageKey) !== 'false';
    } catch {
      return true; // storage blocked — groups just default open
    }
  });

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      // collapse still works for this page-load; it just won't persist
    }
  };

  return (
    <section className="rail-group" data-rail-group={slug}>
      <button type="button" className="rail-group-header" aria-expanded={expanded} onClick={toggle}>
        <span className="field-label rail-group-title">{title}</span>
        {badge && <span className="rail-group-badge">{badge}</span>}
        {status}
        <span className="rail-group-chevron" aria-hidden="true">
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && <div className="rail-group-body">{children}</div>}
    </section>
  );
}

export interface ProjectRailBodyProps {
  invoiceDraft?: InvoiceDraftResponse | null;
  invoiceAction?: React.ReactNode;
  project: ProjectDetailRow;
  dashboardSlug: string | null;
  today: string;
  mode: MoneyMode;
  moneyTotal: number | null;
  invoices: InvoiceRow[];
  /** The project client's live contact register + the resolved
      picked → override → default cascade (multi-contacts, mockup §3b). */
  clientContacts: ClientContactRow[];
  resolvedContact: ResolvedProjectContact;
  clients: ClientWithCounts[];
  clientOpen: boolean;
  setClientOpen: Dispatch<SetStateAction<boolean>>;
  openClientPicker: () => void;
  crewBookings: ProjectCrewWithName[];
  crewRegister: CrewContactRow[];
  crewConflicts: Record<string, ProjectCrewConflict[]>;
  /** null = at least one booking has a half day with no half-day rate on
      file (bookingCostCents' error state) — rendered as "—", never NaN or a
      silently-dropped booking. */
  crewTotalCents: number | null;
  crewPeopleCount: number;
  crewDayCount: number;
  crewHalfCount: number;
  deliverables: ProjectDeliverableRow[];
  artifacts: ProjectArtifactRow[];
  onReviewArtifactUpdated?: (artifact: ProjectArtifactRow) => void;
  unlinked: UnlinkedArtifactCandidate[];
  toolIntegrations: { scripts: boolean; callsheets: boolean; reviews: boolean };
  phaseOpen: boolean;
  setPhaseOpen: Dispatch<SetStateAction<boolean>>;
  socialOpen: boolean;
  setSocialOpen: Dispatch<SetStateAction<boolean>>;
  waitingBusy: boolean;
  waitingNote?: string | null;
  deliveryLinkBusy: boolean;
  deliveryLinkNote: string | null;
  setCreateModal: Dispatch<SetStateAction<'script' | 'callsheet' | 'review' | null>>;
  patch: (fields: Record<string, unknown>) => Promise<void>;
  waitingSendNow: () => Promise<void>;
  waitingSnooze: () => Promise<void>;
  testimonialAction: (action: 'responded' | 'skip' | 'reopen') => Promise<void>;
  patchCrewBooking: (bookingId: string, fields: Record<string, unknown>) => Promise<void>;
  removeCrewBooking: (bookingId: string) => Promise<void>;
  addCrewBooking: (fields: CrewBookingDraft) => Promise<void>;
  copyDeliveryLink: () => Promise<void>;
  unlinkArtifact: (artifactId: string) => Promise<void>;
  adoptArtifact: (candidate: UnlinkedArtifactCandidate) => Promise<void>;
  onTrashProject: (cascadeInvoices: boolean) => Promise<void> | void;
}

/** Presentational seam for the complete project-detail rail. ProjectDetail
    owns all data and handlers; this component only arranges the existing
    controls into the approved collapsible groups (plus D1's Proposals & quotes). */
export function ProjectRailBody({
  invoiceAction,
  invoiceDraft,
  project,
  dashboardSlug,
  today,
  mode,
  moneyTotal,
  invoices,
  clientContacts,
  resolvedContact,
  clients,
  clientOpen,
  setClientOpen,
  openClientPicker,
  crewBookings,
  crewRegister,
  crewConflicts,
  crewTotalCents,
  crewPeopleCount,
  crewDayCount,
  crewHalfCount,
  deliverables,
  artifacts,
  onReviewArtifactUpdated,
  unlinked,
  toolIntegrations,
  phaseOpen,
  setPhaseOpen,
  socialOpen,
  setSocialOpen,
  waitingBusy,
  waitingNote = null,
  deliveryLinkBusy,
  deliveryLinkNote,
  setCreateModal,
  patch,
  waitingSendNow,
  waitingSnooze,
  testimonialAction,
  patchCrewBooking,
  removeCrewBooking,
  addCrewBooking,
  copyDeliveryLink,
  unlinkArtifact,
  adoptArtifact,
  onTrashProject,
}: ProjectRailBodyProps) {
  const [transientCustomContact, setTransientCustomContact] = useState(false);
  useEffect(() => setTransientCustomContact(false), [project.id]);
  const customContactMode = transientCustomContact || projectContactPickerMode(project) === 'custom';
  const saveCustomContactField = async (field: 'contact_name' | 'contact_email' | 'contact_phone', value: string | null) => {
    await patch({ [field]: value });
    if (value !== null) setTransientCustomContact(false);
  };

  return (
    <div className="detail-rail">
      <div className="rail-groups">
        <RailGroup slug="status-dates" title="Status & dates">
          <div className="field" data-rail-control="phase">
            <div className="field-label">Phase</div>
            <div className="dropdown-anchor">
              <span className="select-pill" onClick={() => setPhaseOpen((open) => !open)}>
                <Pill tone={pillTone(project.phase)}>{PHASE_LABELS[project.phase]}</Pill>
                <span className="caret">▾</span>
              </span>
              {phaseOpen && (
                <Dropdown
                  items={PROJECT_PHASES.map((phase) => ({
                    value: phase,
                    label: PHASE_LABELS[phase],
                    dotColor: PHASE_DOTS[phase],
                  }))}
                  value={project.phase}
                  onSelect={(phase) => patch({ phase })}
                  onClose={() => setPhaseOpen(false)}
                />
              )}
            </div>
          </div>
          <div className="field" data-rail-control="brand">
            <div className="field-label">Brand</div>
            <Seg<Brand>
              options={[
                { value: 'hsp', label: 'HSP' },
                { value: 'ss', label: 'SS' },
              ]}
              value={project.brand}
              onChange={(brand) => patch({ brand })}
            />
          </div>
          <div className="field rail-inline-fields" data-rail-control="dates">
            <div className="rail-inline-cell">
              <div className="field-label">Due</div>
              <InlineDate value={project.due_date} onSave={(due_date) => patch({ due_date })} />
            </div>
            <div className="rail-inline-cell">
              <div className="field-label">Shoot</div>
              <InlineDate
                value={project.shoot_date}
                suffix={project.shoot_date && project.shoot_date < today ? ' · done' : undefined}
                onSave={(shoot_date) => patch({ shoot_date })}
              />
              {project.shoot_date && (
                <div className="rail-date-end" title="Last day of the shoot">
                  <span className="arrow">→</span>
                  <InlineDate
                    value={project.shoot_end_date}
                    seed={project.shoot_date}
                    compact
                    allowClear
                    onSave={(shoot_end_date) => patch({ shoot_end_date })}
                  />
                </div>
              )}
            </div>
            <div className="rail-inline-cell">
              <div className="field-label">Edit</div>
              <InlineDate value={project.edit_date} onSave={(edit_date) => patch({ edit_date })} />
              {project.edit_date && (
                <div className="rail-date-end" title="Last day of the edit">
                  <span className="arrow">→</span>
                  <InlineDate
                    value={project.edit_end_date}
                    seed={project.edit_date}
                    compact
                    allowClear
                    onSave={(edit_end_date) => patch({ edit_end_date })}
                  />
                </div>
              )}
            </div>
          </div>
        </RailGroup>

        <RailGroup slug="money" title="Money" badge={mode === 'incl' ? 'inc GST' : 'ex GST'} status={<ProjectMoneyStatus status={invoiceDraft?.strip.quote_status ?? project.quote_status} />}>
          <ProjectMoneyRows draft={invoiceDraft} invoices={invoices} today={today} mode={mode} quoteStatus={project.quote_status}
            quote={<InlineMoney cents={project.value_cents ?? moneyTotal} onSave={(value_cents) => patch({ value_cents })} />}
            action={invoiceAction} crew={crewBookings.length > 0 && <div className="mr-foot" title={crewTotalCents === null ? CREW_COST_ERROR_TITLE : undefined}>
              <span>Crew cost</span><span>{formatMoney(crewTotalCents)}</span>
            </div>} />
        </RailGroup>

        <RailGroup slug="contact" title="Contact">
          <div className="field rail-inline-fields rail-contact-row" data-rail-control="client-dashboard">
            <div className="rail-client-cell">
              <div className="field-label">Client</div>
              <div
                className="field-value"
                title={project.client_name}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <div className="dropdown-anchor" style={{ minWidth: 0 }}>
                  <span className="select-pill" onClick={openClientPicker} style={{ maxWidth: '100%' }}>
                    <span className="rail-client-name">{project.client_name}</span>
                    <span className="caret">▾</span>
                  </span>
                  {clientOpen && (
                    <Dropdown
                      items={clients.map((client) => ({ value: client.id, label: client.name }))}
                      value={project.client_id}
                      onSelect={(client_id) => {
                        if (client_id !== project.client_id) void patch({ client_id });
                      }}
                      onClose={() => setClientOpen(false)}
                      searchable
                    />
                  )}
                </div>
                <Link to={`/clients/${project.client_id}`} style={{ color: 'var(--accent)', fontSize: 10 }}>
                  open ↗
                </Link>
              </div>
            </div>
            <div className="rail-dashboard-cell">
              <div className="field-label">Dashboard</div>
              {dashboardSlug ? (
                <div className="field-value mono">
                  <a href={`${DASHBOARD_BASE}/${dashboardSlug}`} target="_blank" rel="noreferrer">
                    /{dashboardSlug} ↗
                  </a>
                </div>
              ) : (
                <div className="field-value dim">— none</div>
              )}
            </div>
          </div>
          {clientContacts.length > 0 ? (
            <>
              <div className="field-label">Contact</div>
              <ContactPicker
                project={project}
                clientContacts={clientContacts}
                resolvedContact={resolvedContact}
                isCustom={customContactMode}
                onModeChange={setTransientCustomContact}
                patch={patch}
              />
              <div className="field" data-rail-control="contact-lines" style={{ borderBottom: 'none' }}>
                {customContactMode ? (
                  <>
                    <ContactLine
                      value={project.contact_name}
                      fallback={transientCustomContact ? null : project.client_contact_name}
                      onSave={(contact_name) => saveCustomContactField('contact_name', contact_name)}
                    />
                    <ContactLine
                      value={project.contact_email}
                      fallback={transientCustomContact ? null : project.client_contact_email}
                      mono
                      accent
                      onSave={(contact_email) => saveCustomContactField('contact_email', contact_email)}
                    />
                    <ContactLine
                      value={project.contact_phone}
                      fallback={transientCustomContact ? null : project.client_contact_phone}
                      mono
                      onSave={(contact_phone) => saveCustomContactField('contact_phone', contact_phone)}
                    />
                  </>
                ) : (
                  <>
                    <div className="field-value">{resolvedContact.name ?? '—'}</div>
                    <div className="field-value mono" style={{ color: 'var(--accent)', marginTop: 2 }}>
                      {resolvedContact.email ?? '—'}
                    </div>
                    <div className="field-value mono dim" style={{ marginTop: 2 }}>
                      {resolvedContact.phone ?? '—'}
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="field" data-rail-control="contact-lines">
              <div className="field-label">Contact</div>
              <ContactLine
                value={project.contact_name}
                fallback={project.client_contact_name}
                onSave={(contact_name) => patch({ contact_name })}
              />
              <ContactLine
                value={project.contact_email}
                fallback={project.client_contact_email}
                mono
                accent
                onSave={(contact_email) => patch({ contact_email })}
              />
              <ContactLine
                value={project.contact_phone}
                fallback={project.client_contact_phone}
                mono
                onSave={(contact_phone) => patch({ contact_phone })}
              />
            </div>
          )}
        </RailGroup>

        <RailGroup
          slug="crew"
          title="Crew"
          badge={`${crewPeopleCount} ${crewPeopleCount === 1 ? 'person' : 'people'}`}
        >
          <div data-rail-control="crew">
            {crewBookings.map((booking) => (
              <CrewEntryRow
                key={booking.id}
                booking={booking}
                conflicts={crewConflicts[booking.id] ?? []}
                onSave={(fields) => patchCrewBooking(booking.id, fields)}
                onRemove={() => removeCrewBooking(booking.id)}
              />
            ))}
            {crewBookings.length === 0 && <div className="field-value dim">No crew booked yet.</div>}
            <AddCrewBooking register={crewRegister} today={today} onAdd={addCrewBooking} />
            {crewBookings.length > 0 && (
              <div className="crew-total">
                <span className="lbl">Total crew cost</span>
                <span className="val" title={crewTotalCents === null ? CREW_COST_ERROR_TITLE : undefined}>
                  {formatMoney(crewTotalCents)}
                </span>
                <div className="sub">{crewFooterSub(crewPeopleCount, crewDayCount, crewHalfCount)}</div>
              </div>
            )}
          </div>
        </RailGroup>

        <RailGroup slug="tools-delivery" title="Tools & delivery">
          <div className="field" data-rail-control="tools">
            {TOOL_MAP.map(({ tool, urlField, route, label }) => {
              const rows = artifacts.filter((artifact) => artifact.tool === tool);
              const canCreate = tool === 'script' || tool === 'callsheet' || tool === 'review';
              const configured = {
                script: toolIntegrations.scripts,
                callsheet: toolIntegrations.callsheets,
                proposal: false,
                review: toolIntegrations.reviews,
              }[tool];
              return (
                <Fragment key={tool}>
                  {rows.length === 0 ? (
                    <div style={{ marginTop: 2 }}>
                      <div className="field-label">{label}</div>
                      {urlField !== null && (
                        <InlineText
                          value={project[urlField]}
                          mono
                          valueClass="mono"
                          placeholder={`${label} URL…`}
                          display={
                            project[urlField] ? (
                              <Link
                                to={`/tools/${route}?url=${encodeURIComponent(project[urlField]!)}`}
                                onClick={(event) => event.stopPropagation()}
                                style={{ color: 'var(--accent)' }}
                              >
                                {label} →
                              </Link>
                            ) : undefined
                          }
                          onSave={(value) => patch({ [urlField]: value })}
                        />
                      )}
                    </div>
                  ) : (
                    rows.map((artifact) => (
                      <div key={artifact.id} className="art-row">
                        <ArtifactTitle title={artifact.title} fallback={label} />
                        <span className="acts">
                          {artifact.admin_url ? (
                            <Link
                              to={`/tools/${route}?url=${encodeURIComponent(artifact.admin_url)}`}
                              style={{ color: 'var(--accent)', flexShrink: 0 }}
                            >
                              Open ↗
                            </Link>
                          ) : artifact.public_url ? (
                            <a
                              href={artifact.public_url}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: 'var(--accent)', flexShrink: 0 }}
                            >
                              Open ↗
                            </a>
                          ) : null}
                          <a
                            className="group-link"
                            style={{ cursor: 'pointer', flexShrink: 0 }}
                            onClick={() => unlinkArtifact(artifact.id)}
                          >
                            Unlink
                          </a>
                        </span>
                        <span className="artifact-meta">
                          <span className="art-type">
                            {label}{artifactTypeLabel(artifact.tool, artifact.artifact_type) && ` · ${artifactTypeLabel(artifact.tool, artifact.artifact_type)}`}
                          </span>
                          {artifact.status && (
                            <Pill tone={artifactStatusTone(artifact.status)}>{artifactStatusLabel(artifact.status)}</Pill>
                          )}
                        </span>
                        {artifact.tool === 'review' && <ReviewArtifactActions artifact={artifact} dashboardSlug={dashboardSlug}
                          configured={toolIntegrations.reviews} onUpdated={onReviewArtifactUpdated} />}
                      </div>
                    ))
                  )}
                  {canCreate && (
                    <div style={{ marginTop: 2 }}>
                      <a
                        className="group-link"
                        style={{
                          cursor: configured ? 'pointer' : 'not-allowed',
                          opacity: configured ? 1 : 0.5,
                        }}
                        title={configured ? undefined : `${label} tool integration not configured yet.`}
                        onClick={() => configured && setCreateModal(tool)}
                      >
                        + New {label.toLowerCase()}
                      </a>
                    </div>
                  )}
                </Fragment>
              );
            })}
            {unlinked.map((candidate, index) => (
              <div key={`${candidate.tool}-${candidate.external_id ?? candidate.url ?? index}`} style={{ marginTop: 8 }}>
                <span className="field-label" style={{ margin: 0 }}>
                  {ARTIFACT_TOOL_LABELS[candidate.tool]}
                  {candidate.title ? ` · ${candidate.title}` : ''}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                  <span className="field-value dim" style={{ fontStyle: 'italic', flex: 1 }}>
                    Found on dashboard — link to this project?
                  </span>
                  <a
                    className="group-link"
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => adoptArtifact(candidate)}
                  >
                    Adopt
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="field" data-rail-control="delivery">
            <div className="field-label">Delivery page</div>
            <div className="field-value dim">{deliverables.filter((row) => row.url).length} of {deliverables.length} deliverables have a delivery link.</div>
            <div style={{ marginTop: 10 }}>
              <div className="field-label" style={{ marginBottom: 2 }}>
                Gated by
              </div>
              <InlineSelect
                items={[
                  { value: 'none', label: 'No gate' } as DropdownItem<string>,
                  ...invoices.map(
                    (invoice): DropdownItem<string> => ({
                      value: invoice.id,
                      label: `${formatInvoiceNumber(invoice.invoice_number)} · ${formatMoney(invoice.amount_cents)}`,
                    }),
                  ),
                ]}
                value={project.gating_invoice_id ?? 'none'}
                onSave={(value) => patch({ gating_invoice_id: value === 'none' ? null : value })}
              />
            </div>
            <button
              className="btn block"
              style={{ marginTop: 10 }}
              onClick={copyDeliveryLink}
              disabled={deliveryLinkBusy}
            >
              {deliveryLinkBusy ? 'Creating…' : (deliveryLinkNote ?? 'Copy delivery link')}
            </button>
          </div>

          <DrivePicker key={project.id} projectId={project.id} />
        </RailGroup>

        <RailGroup slug="proposals-quotes" title="Proposals & quotes">
          <div className="field" data-rail-control="proposals-quotes">
            <DocumentSendsRail
              filter={{ project_id: project.id }}
              defaults={{ project_id: project.id, client_id: project.client_id, kind: 'proposal' }}
            />
          </div>
        </RailGroup>

        <RailGroup slug="automations" title="Automations">
          <div className="field waiting-field" data-rail-control="waiting-on-client">
            <div className="field-label">Waiting on client</div>
            <button
              type="button"
              className={`chip${project.waiting_on_client_since !== null ? '' : ' accent'}`}
              disabled={waitingBusy}
              onClick={() => patch({ waiting_on_client: project.waiting_on_client_since === null })}
            >
              {project.waiting_on_client_since !== null ? 'Clear — client replied' : 'Mark waiting'}
            </button>
            {project.waiting_on_client_since !== null && (
              <div className="waiting-detail">
                <div className="field-value">
                  Since {formatDate(melbourneDate(project.waiting_on_client_since))} ·{' '}
                  {Math.max(0, calendarDaysBetween(melbourneDate(project.waiting_on_client_since), today))}d
                </div>
                <div className="field-value dim">
                  Nudge {project.waiting_nudge_count} of 3 · {waitingNextStatus(project, today)}
                </div>
                <div className="waiting-actions">
                  <button type="button" className="chip accent" disabled={waitingBusy} onClick={waitingSendNow}>
                    {waitingBusy ? 'Sending…' : 'Send now'}
                  </button>
                  <button type="button" className="chip" disabled={waitingBusy} onClick={waitingSnooze}>
                    Snooze +1w
                  </button>
                  {waitingNote && (
                    <span style={{ fontSize: 12, color: 'var(--text-dim)', alignSelf: 'center' }}>{waitingNote}</span>
                  )}
                </div>
              </div>
            )}
          </div>
          {project.delivered_date && (
            <div className="field" data-rail-control="testimonial">
              <div className="field-label">Testimonial</div>
              {project.testimonial_closed_at ? (
                <div className="field-value" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Pill tone={project.testimonial_closed_reason === 'responded' ? 'accent' : undefined}>
                    {project.testimonial_closed_reason === 'responded' ? project.testimonial_video_key ? 'Video received' : '✓ Responded' : 'Not asking'}
                  </Pill>
                  {project.testimonial_video_key && (
                    <a className="group-link" target="_blank" rel="noopener noreferrer"
                      href={testimonialMediaAdminLink(project.testimonial_video_key)}>
                      Play video
                    </a>
                  )}
                  <a className="group-link" style={{ cursor: 'pointer' }} onClick={() => testimonialAction('reopen')}>
                    Reopen
                  </a>
                </div>
              ) : (
                <>
                  <div className="field-value dim">
                    {project.testimonial_reminder_at
                      ? 'Reminder sent'
                      : project.testimonial_requested_at
                        ? 'Ask sent'
                        : 'Ask scheduled'}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginTop: 3 }}>
                    <a className="group-link" style={{ cursor: 'pointer' }} onClick={() => testimonialAction('responded')}>
                      Mark responded
                    </a>
                    <a className="group-link" style={{ cursor: 'pointer' }} onClick={() => testimonialAction('skip')}>
                      Don't ask
                    </a>
                  </div>
                </>
              )}
            </div>
          )}
          {project.testimonial_video_key && <div className="field-value dim" style={{ marginTop: 4, marginBottom: 12 }}>
            {testimonialPermissionLabel(project.testimonial_permission)}
          </div>}
          <div className="field" data-rail-control="social-status">
            <div className="field-label">Social status</div>
            <div className="dropdown-anchor">
              <span className="select-pill" onClick={() => setSocialOpen((open) => !open)}>
                {project.social_status ? (
                  <Pill tone={socialTone(project.social_status)}>{SOCIAL_STATUS_LABELS[project.social_status]}</Pill>
                ) : (
                  <span className="field-value dim">—</span>
                )}
                <span className="caret">▾</span>
              </span>
              {socialOpen && (
                <Dropdown
                  items={SOCIAL_STATUSES.map((status) => ({ value: status, label: SOCIAL_STATUS_LABELS[status] }))}
                  value={project.social_status}
                  onSelect={(social_status) => patch({ social_status })}
                  onClose={() => setSocialOpen(false)}
                />
              )}
            </div>
          </div>
          {/* Project-level social restriction (Social tab, 2026-08): blocks
              every asset under this project. Clearing it is only possible
              here — the assets themselves show it as read-only. */}
          <div className="field" data-rail-control="social-restricted">
            <div className="field-label">No social use</div>
            <button
              type="button"
              className={`chip${project.social_restricted === 1 ? ' danger' : ''}`}
              onClick={() =>
                patch(
                  project.social_restricted === 1
                    ? { social_restricted: 0, social_restricted_note: null }
                    : { social_restricted: 1 },
                )
              }
            >
              {project.social_restricted === 1 ? 'Restricted — allow social' : 'Flag no social use'}
            </button>
            {project.social_restricted === 1 && (
              <InlineText
                value={project.social_restricted_note}
                placeholder="Why is this off limits?"
                onSave={(social_restricted_note) => patch({ social_restricted_note })}
              />
            )}
          </div>
        </RailGroup>
      </div>

      <div className="meta-stamp" data-rail-control="meta-stamp">
        CREATED {formatDate(melbourneDate(project.created_at)).toUpperCase()}
        <br />
        UPDATED {formatDate(melbourneDate(project.updated_at)).toUpperCase()}
      </div>
      <div data-rail-control="trash-action">
        <TrashProjectAction liveInvoices={invoices.length} onTrash={onTrashProject} />
      </div>
    </div>
  );
}

/** Project trash with invoice coupling (phase 13): the usual two-step
    confirm, plus — when the project has live linked invoices — a third step
    asking whether they ride along into the trash with the same stamp. */
function TrashProjectAction({
  liveInvoices,
  onTrash,
}: {
  liveInvoices: number;
  onTrash: (cascadeInvoices: boolean) => void;
}) {
  const [step, setStep] = useState<'idle' | 'armed' | 'invoices'>('idle');
  useEffect(() => {
    if (step !== 'armed') return;
    const t = setTimeout(() => setStep('idle'), 4000);
    return () => clearTimeout(t);
  }, [step]);

  if (step === 'invoices') {
    return (
      <div style={{ marginTop: 18 }}>
        <div className="field-label" style={{ marginBottom: 8 }}>
          Also move {liveInvoices} linked invoice{liveInvoices === 1 ? '' : 's'} to trash?
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn danger" onClick={() => onTrash(true)}>
            Project + invoice{liveInvoices === 1 ? '' : 's'}
          </button>
          <button className="btn" onClick={() => onTrash(false)}>
            Project only
          </button>
          <button className="btn" onClick={() => setStep('idle')}>
            Cancel
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      type="button"
      className="trash-action"
      onClick={() => {
        if (step === 'idle') setStep('armed');
        else if (liveInvoices > 0) setStep('invoices');
        else onTrash(false);
      }}
    >
      {step === 'armed' ? 'Click again to confirm' : 'Move project to trash'}
    </button>
  );
}
